
import React, { useState } from 'react';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { 
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface DateTimePickerProps {
  date: Date | undefined;
  onDateChange: (date: Date | undefined) => void;
  time: string;
  onTimeChange: (time: string) => void;
  disabled?: Date[] | ((date: Date) => boolean);
  label?: string;
  minDate?: Date;
  allowPastDates?: boolean; // Allow past dates (for editing existing appointments)
  disableDatePicker?: boolean; // Disable date picker (but keep time input enabled)
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  date,
  onDateChange,
  time,
  onTimeChange,
  disabled = [],
  label,
  minDate,
  allowPastDates = false,
  disableDatePicker = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Combine disabled dates with past dates (only if not allowing past dates)
  const getDisabledDates = () => {
    if (allowPastDates) {
      // When editing, only use the provided disabled dates
      return disabled;
    }
    
    const today = minDate || new Date();
    today.setHours(0, 0, 0, 0);
    
    const isPastDate = (dateToCheck: Date) => {
      const checkDate = new Date(dateToCheck);
      checkDate.setHours(0, 0, 0, 0);
      return checkDate < today;
    };
    
    if (Array.isArray(disabled)) {
      return [...disabled, isPastDate];
    } else if (typeof disabled === 'function') {
      return (dateToCheck: Date) => disabled(dateToCheck) || isPastDate(dateToCheck);
    } else {
      return isPastDate;
    }
  };

  // Handle date selection - prevent clearing when clicking already-selected date
  const handleDateSelect = (newDate: Date | undefined) => {
    if (newDate) {
      onDateChange(newDate);
      setIsOpen(false); // Close popover after selection
    }
    // Don't clear date if newDate is undefined (clicking on already-selected date)
  };
  
  // Validate time - ensure it's not in the past if date is today
  const handleTimeChange = (newTime: string) => {
    onTimeChange(newTime);
  };
  
  return (
    <div className="flex flex-col gap-2 w-full">
      {label && <div className="text-sm font-medium leading-none">{label}</div>}
      <div className="grid grid-cols-2 gap-2">
        <div className="relative">
          <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
              <Button
                variant={"outline"}
                disabled={disableDatePicker}
                className={cn(
                  "w-full pl-3 text-left font-normal",
                  !date && "text-muted-foreground",
                  disableDatePicker && "cursor-not-allowed opacity-50"
                )}
              >
                {date ? (
                  format(date, "PPP")
                ) : (
                  <span>Pick a date</span>
                )}
                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
              </Button>
            </PopoverTrigger>
            {!disableDatePicker && (
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={handleDateSelect}
                  initialFocus
                  disabled={getDisabledDates()}
                  className="p-3 pointer-events-auto"
                  modifiers={{
                    unavailable: Array.isArray(disabled) ? disabled : [],
                  }}
                  modifiersClassNames={{
                    unavailable: "bg-[#E5DEFF] text-gray-500 line-through",
                  }}
                />
              </PopoverContent>
            )}
          </Popover>
        </div>
        
        <Input 
          type="time" 
          value={time} 
          onChange={(e) => handleTimeChange(e.target.value)} 
          className="h-10"
        />
      </div>
    </div>
  );
};

export default DateTimePicker;
